/**
 * Pipeline warm-up (2026-08-01 frame-drop pass).
 *
 * THE PROBLEM. Under WebGPU every unique (material, geometry layout, pass)
 * combination becomes a render pipeline, and three compiles it lazily — on the
 * frame that first draws it. In a run that means:
 *
 *  - the first frame a given vehicle MODEL enters the frame,
 *  - the first frame a given BIOME's props enter the frame (each seam, first
 *    cycle),
 *  - the first frame particles/debris/legs/crash meshes draw,
 *  - and the first frame each of those enters the SHADOW pass too (a shadow
 *    pipeline is a separate compile from the colour pipeline).
 *
 * Every one of those was a multi-frame hitch — precisely the "frame drops
 * sometimes" the playtest reported, because they are spread across the run
 * rather than clustered at load.
 *
 * THE FIX. Before the loop starts (while the LOADING overlay still covers the
 * canvas), park one instance of EVERY variant in the real scene — real lights,
 * real fog, real shadow map, so the compiled pipelines are the ones gameplay
 * uses — and drive `renderer.compileAsync` plus one real frame. Then put
 * everything back.
 *
 * Cost is paid once, in the load screen, where it is invisible.
 *
 * 2026-08-02 (second frame-drop pass). A GPUDevice.createRenderPipeline probe
 * on the bench showed the warm-up was still missing two whole classes, and both
 * were still compiling mid-run, 60-170 ms a frame, inside `renderer.render`:
 *
 *  - EVERY BIOME CHUNK SLOT, not just slot 0 (three keys a pipeline per render
 *    object, so each of the recycled slots pays its own first-draw compile),
 *    and every biome-LOCAL SLICE, since a chunk's props are a function of its
 *    index inside the biome. See BiomeSystem.warmup. (Obsolete since the
 *    program-count pass below: there is no slot axis left.)
 *  - THE OTHER TWO KICK-LEG VARIANTS (right-opaque and left-fade). See
 *    KickLeg.warmup.
 *
 * Measured over matched 35 s bench runs (perfbench.html, ~5000 frames, a kick
 * every 1.5 s): mid-run pipeline compiles 133 -> 83, frames over 33 ms 12 -> 3,
 * p99 13-16 ms -> 10 ms. The price is LOADING time: 231 pipelines / 3.0 s
 * before, 624 / 7.5 s after, on an M-series Mac.
 *
 * 2026-08-02 (crash-hitch pass). The same probe caught a third class, the one
 * behind the owner's "massive frame drop on collision — it froze, then showed
 * the aftermath": THE CRASH-CYCLIST CLONE. It is added to the scene at load but
 * kept `visible = false` until the player crashes, and it carries its own CLONED
 * (matted) materials, so `compileAsync` and the draw both skipped it and its two
 * pipelines — colour + shadow — were compiled inside the crash frame. Measured
 * over 5 fresh page loads: first crash 2 compiles / 32-42 ms, second crash in
 * the same load 0 compiles / ~7 ms. See CrashSequence.warmup.
 *
 * 2026-08-02 (LOAD-TIME pass). Coverage was right and the load screen was 7-9 s.
 * The owner asked for it back. Profiling it — per-phase wall clock
 * (`window.__warmupProfile` / `window.__bootProfile`) against a probe that times
 * every GPUDevice call perfbench.html makes — moved the blame somewhere nobody
 * had looked:
 *
 *   createRenderPipeline + createRenderPipelineAsync   596 calls,     8 ms
 *   createShaderModule                                 612 calls,    26 ms
 *   createBindGroup / createBuffer / …                6500 calls,    30 ms
 *   three's Nodes.getForRender (WGSL generation)       441 BUILDS, 5000 ms
 *
 * THE LOAD SCREEN IS NOT PIPELINE COMPILATION. The driver's share of a 5.4 s
 * warm-up is under 60 ms. All of it is three generating shader code in
 * JavaScript, ~12 ms per program — and the program count is set by ONE line in
 * `RenderObject.getMaterialCacheKey`:
 *
 *   if ( object.isInstancedMesh || object.count > 1 ) cacheKey += object.uuid;
 *
 * Every InstancedMesh gets its OWN shader program, because its instance-matrix
 * uniform buffer is baked into the generated WGSL by node id (diffing two
 * `vertex_cityFurniture` modules: 147 lines, 4 differ, all of them the name
 * `NodeBuffer_144283` vs `NodeBuffer_160257`). This scene has 413 instanced
 * meshes — 6 biomes x 8 recycled chunk slots x ~9 prop archetypes — so it has
 * ~413 programs to build, and that product IS the load time.
 *
 * What this pass could therefore fix was the SHAPE, not the unit cost:
 *
 *  - `compileParallel` (below). three's `compileAsync` awaits the completion
 *    callback of EACH pipeline before requesting the next; the compile is free
 *    but the round trip is not. Issuing the batch together is worth ~3.4 s.
 *  - ONE CONFIGURATION INSTEAD OF TEN PASSES. Every biome prop mesh, the whole
 *    vehicle pool, both legs, the crash clone, the coins and the particles are
 *    posed together; positions are irrelevant to a program or a pipeline key, so
 *    the subjects simply overlap. Only variants that cannot share one material
 *    slot (leg opaque/fade, the vehicle paint table) take a second config.
 *  - THE SLICE ROTATION IS NOW FREE (BiomeSystem.warmup): with every slot posed
 *    in every pass, passes 1-7 only add the few PropFields the old diagonal
 *    never populated. 17 ms and 8 ms of render each. (Superseded: the biome
 *    warm-up is a single pass since the program-count pass.)
 *  - WARM AT 64x64. The warm-up frames are never seen and a pipeline key does
 *    not depend on the drawing buffer's SIZE, so the fragment cost of 48 stacked
 *    chunks is taken out of every pass.
 *
 * Measured, warm cache, 5 loads each: page load -> overlay dismissed
 * 7.20-7.47 s before, 5.0-5.3 s after, with MORE compiled at load (628 -> 833
 * pipelines) and FEWER mid-run compiles over a 2400 m six-biome lap (270 -> 128).
 * The floor is honest: ~413 instanced meshes x ~12 ms of three-side WGSL
 * generation.
 *
 * 2026-08-02 (PROGRAM-COUNT pass). That floor is now gone, and it went where
 * the note above said it would have to: biomes.ts. A content chunk's pose
 * factorises into a per-slot rigid transform (a pure function of its road
 * anchor, so it only changes on a recycle) and ONE translation shared by every
 * chunk of every biome, so the slot axis can be baked into the instance
 * matrices and the 6 biomes x 10 recycled slots x ~7 archetypes collapse to one
 * InstancedMesh per (biome, archetype). See the SharedField note in biomes.ts.
 *
 *   instanced meshes (= shader programs)   413  ->   44
 *   pipelines compiled at load             859  ->  134
 *   page load -> overlay dismissed     6.59-6.76 s -> 2.43-2.62 s (5 loads each)
 *   warm-up phase                      5.97-6.11 s -> 1.89-1.95 s
 *   mid-run compiles, one 2400 m lap        138  ->    0
 *   frame ms p50 / p95 / p99            5.9/8.2/9.6 -> 5.2/7.1/7.9
 *   frames over 33 ms per 20k              12    ->    0
 *
 * The biome half of this file collapses with it: the eight-pass (slot, slice)
 * rotation is one pass, because there is no per-slot mesh left to warm and the
 * ten recycled slots already cover all eight biome-local slices at once.
 *
 * ROOT CAUSE OF THE 128 (now 0). The probe attributed the surviving mid-run
 * compiles precisely: 2 colour pipelines per (biome, archetype) plus their
 * shadow twins, and NEVER any other number. That is the signature of an
 * off-by-two — the old `BiomeSystem.warmup` looped `i < CHUNKS_PER_BIOME` (8)
 * over an array of `CHUNK_COUNT` (10) recycled slots, so slots 8 and 9 were
 * never posed and every archetype paid for them the first time the run handed
 * them a biome. The rewrite iterates the slots, and the collapse to one mesh
 * per archetype removes the axis the bug lived on.
 *
 * MEASURE THE WHOLE THING, NOT ONE KNOB. Dropping `compileAsync` altogether and
 * letting `renderer.render` compile everything synchronously lands within 10 ms
 * of `compileParallel` (5372 ms vs 5362 ms on the same machine) — which is the
 * same statement as above from the other side: the async path only pays off
 * once its per-object stall is gone.
 *
 * 2026-08-01 (spawn-clone pass). The vehicle half of this no longer warms
 * throwaway clones — it warms THE POOL. Every view VehicleView will ever draw
 * is pre-built at load, so parking the real objects here means their render
 * objects, per-object uniform buffers and bind groups (colour AND shadow pass)
 * are built now and reused for the whole run; a spawn is then a visibility
 * toggle over a Mesh the GPU has already seen. See VehicleView.warmup.
 */

import type { PerspectiveCamera, Scene, WebGPURenderer } from "three/webgpu";
import { Vector2 } from "three/webgpu";
import type { VehicleView, VehicleWarmup } from "./vehicleView";
import type { CoinView, CoinWarmup } from "./coins";
import type { BiomeSystem, BiomeWarmup } from "./biomes";
import type { LegWarmup } from "./kickLeg";
import type { CrashWarmup } from "./crashSequence";
import type { JuiceCoordinator } from "./juiceCoordinator";

/**
 * Dev-only segmentation hook. `perfbench.html` installs
 * `window.__pipeMark(label)` alongside its GPUDevice.createRenderPipeline probe,
 * so the pipeline log can be split per warm-up phase. No-op in the shipped page.
 */
function mark(label: string): void {
  const m = (globalThis as { __pipeMark?: (s: string) => void }).__pipeMark;
  if (typeof m === "function") m(label);
}

/** Per-phase wall clock for the warm-up, published on `window.__warmupProfile`. */
const profile: Array<{ phase: string; ms: number }> = [];
function phase<T>(label: string, fn: () => T): T {
  mark(label);
  const t0 = performance.now();
  const out = fn();
  profile.push({ phase: label, ms: performance.now() - t0 });
  return out;
}
async function phaseAsync(label: string, fn: () => Promise<void>): Promise<void> {
  mark(label);
  const t0 = performance.now();
  await fn();
  profile.push({ phase: label, ms: performance.now() - t0 });
}

/**
 * `renderer.compileAsync`, but without three's per-render-object stall.
 *
 * WHY (2026-08-02 load-time pass). three r185's `compileAsync` walks the render
 * list and then processes it strictly one render object at a time:
 *
 *   for ( const item of compilationPromises ) {
 *     await this._nodes.getForRenderAsync( renderObject );
 *     ...
 *     this._pipelines.getForRender( renderObject, pipelinePromises );
 *     if ( pipelinePromises.length > 0 ) await Promise.all( pipelinePromises );
 *     await yieldToMain();
 *   }
 *
 * The `await Promise.all` is the expensive one. It is NOT waiting for a shader
 * to compile — the probe in perfbench.html times every GPUDevice call and all
 * 596 pipeline creations together come to 8 ms — it is waiting for Dawn to
 * deliver the completion callback, once per pipeline, and paying that latency
 * end to end because nothing else is in flight. Measured: 7.6 s of warm-up with
 * it, 4.2 s without, everything else identical.
 *
 * THE FIX, without forking three: swap `_pipelines.getForRender` for a shim
 * that hands the backend a PRIVATE promise array. three's `pipelinePromises`
 * comes back empty, its `await Promise.all` is skipped, and the loop runs to the
 * end while every pipeline is created concurrently. We await the whole batch
 * once at the end, so nothing is rendered before it is ready — the completion
 * guarantee is exactly the one `compileAsync` gives.
 *
 * The one behavioural difference is that the backend's per-pipeline
 * `pushErrorScope('validation')` / `popErrorScope()` pairs now overlap, so a
 * validation error could be reported against the wrong pipeline label. It is
 * still reported, and this only ever runs under the loading overlay.
 *
 * Falls back to the stock `compileAsync` if three's internals ever stop looking
 * like this — the shape is checked, never assumed.
 */
async function compileParallel(
  renderer: WebGPURenderer,
  scene: Scene,
  camera: PerspectiveCamera,
): Promise<void> {
  const pipelines = (renderer as unknown as { _pipelines?: PipelineCache })._pipelines;
  const orig = pipelines?.getForRender;
  if (!pipelines || typeof orig !== "function") {
    await renderer.compileAsync(scene, camera);
    return;
  }
  const batch: Promise<unknown>[] = [];
  pipelines.getForRender = function (
    this: PipelineCache,
    renderObject: unknown,
    promises: Promise<unknown>[] | null,
  ): unknown {
    // The synchronous render path passes `null` and must keep stock behaviour.
    if (promises === null) return orig.call(this, renderObject, promises);
    const sink: Promise<unknown>[] = [];
    const out = orig.call(this, renderObject, sink);
    for (const p of sink) batch.push(p);
    return out;
  };
  try {
    await renderer.compileAsync(scene, camera);
  } finally {
    pipelines.getForRender = orig;
  }
  await Promise.all(batch);
}

/** The slice of three's private pipeline cache this module reaches into. */
interface PipelineCache {
  getForRender(renderObject: unknown, promises: Promise<unknown>[] | null): unknown;
}

export interface WarmupTargets {
  renderer: WebGPURenderer;
  scene: Scene;
  camera: PerspectiveCamera;
  vehicles: VehicleView;
  biomes: BiomeSystem;
  juice: JuiceCoordinator;
  /** The instanced coin field (render/coins.ts). */
  coins: CoinView;
}

/**
 * Compile every gameplay pipeline. Awaits `renderer.compileAsync` and then
 * renders one frame (which is what forces the SHADOW-pass pipelines, since
 * `compileAsync` walks the colour pass only).
 *
 * Safe to call before the first `renderer.render`; never throws — a warm-up
 * failure must not stop the game booting, it only costs the hitches back.
 */
export async function warmupPipelines(t: WarmupTargets): Promise<void> {
  const { renderer, scene, camera, vehicles, biomes, juice, coins } = t;
  // Where a warm-up subject is parked: in front of the camera (which looks down
  // -Z from the rig root) but low enough to sit under the road. Nothing is on
  // screen anyway — the LOADING overlay covers the canvas — and this keeps the
  // meshes inside the shadow frustum so their shadow pipelines compile too.
  const PARK_X = 0, PARK_Y = -0.6, PARK_Z = -14;
  let biomeWarmup: BiomeWarmup | null = null;
  let legWarmup: LegWarmup | null = null;
  let vehicleWarmup: VehicleWarmup | null = null;
  let crashWarmup: CrashWarmup | null = null;
  let coinWarmup: CoinWarmup | null = null;
  // WARM AT POSTAGE-STAMP SIZE. The warm-up frames exist to make three build
  // its render objects and pipelines; nobody sees them (the LOADING overlay is
  // opaque) and the swapchain FORMAT and sample count — the only parts of the
  // canvas that a pipeline key cares about — do not change with its size. So we
  // shrink the drawing buffer to 64x64 for the duration, which takes the
  // fragment cost of drawing 48 stacked biome chunks out of every pass and
  // makes the extra (slot, slice) rotation passes essentially free. `updateStyle
  // = false` keeps the canvas's CSS box (and therefore the page layout)
  // untouched; the size is restored before the first real frame.
  const size = renderer.getDrawingBufferSize(new Vector2());
  const pixelRatio = renderer.getPixelRatio();
  try {
    renderer.setPixelRatio(1);
    renderer.setSize(64, 64, false);
  } catch {
    /* a renderer that will not resize is not a reason to skip the warm-up */
  }
  try {
    // VEHICLES: the pool itself is parked here and drawn, so the render
    // objects, per-object uniform buffers and bind groups warmed up are the
    // ones gameplay uses for the rest of the run — a spawn then reuses a Mesh
    // that the GPU has already seen. See VehicleView.warmup for the pass count.
    vehicleWarmup = phase("build:vehicles", () => vehicles.warmup(PARK_X, PARK_Y, PARK_Z));
    biomeWarmup = phase("build:biomes", () => biomes.warmup());
    // KICK LEG: both legs on screen, over TWO passes, so all four variants
    // (left/right x opaque/fade — the mirrored right leg has its own winding
    // and therefore its own pipeline) are compiled here rather than on the
    // first kick of the run. The legs are camera children, so this covers the
    // shadow pass too (they are inside the shadow frustum at the extended
    // pose). See KickLeg.warmup for the measurement.
    legWarmup = juice.legs.warmup();
    // CRASH CINEMATIC: the crash-cyclist clone carries its OWN (cloned, matted)
    // materials and sits `visible = false` from load until the player crashes,
    // so both its colour and its shadow pipeline used to compile inside the
    // crash frame. Parking it visible here moves that cost to the load screen.
    // See CrashSequence.warmup for the measurement.
    crashWarmup = juice.crash.warmup(PARK_X, PARK_Y, PARK_Z);
    // COINS: one InstancedMesh, one material, so one (colour, shadow) pipeline
    // pair — but it is drawn for the FIRST time somewhere in the middle of a
    // run (the first chain the player rides into), which is exactly the
    // mid-run compile this module exists to prevent. Parked full and visible
    // for the passes below.
    coinWarmup = coins.warmup(PARK_X, PARK_Y, PARK_Z);
    coinWarmup.beginPass(0);
    // Particles: a burst under the world writes both instanced batches (glow +
    // soft) with a non-zero count, which is what makes them compile. The wreck
    // trail is included because its smoke/chips/sparks/SCRAPE DUST all go
    // through the same two batches with different life curves — cheap
    // insurance, no new pipelines. The 2026-08-02 scrape-dust layer is covered
    // by this same `puffWreckTrail` call: it reuses `roadDustConfig` (the
    // impact plume's palette, soft batch and material), so it can never be a
    // first-render compile mid-run.
    juice.particles.burstKick(0, -40, -6);
    juice.particles.burstGroundHit(3, -8);
    juice.particles.burstDown(-3, -8);
    juice.particles.puffWreckTrail(6, -8, 1.2, 1);
    juice.particles.burstCrash(0, -40, -6);
    juice.particles.update(1 / 60, camera);

    // ONE CONFIGURATION PER PASS, NOT ONE SUBJECT PER PASS (2026-08-02
    // load-time pass).
    //
    // Everything above is posed AT THE SAME TIME — all 48 biome chunk slots,
    // the whole vehicle pool, both legs, the crash clone, a full coin field,
    // every particle batch — and compiled together. Positions are irrelevant to
    // a shader program or a pipeline key, so the subjects simply overlap.
    //
    // The old shape was ten passes that each posed ONE subject and stalled on
    // its own `await compileAsync` before the next could start: eight biome
    // passes (one chunk slot each) and two actor passes. Collapsing them cost
    // nothing in coverage and took ~2 s off the load screen, because pass 0 now
    // pays for essentially every shader program in the game and the rest only
    // pick up the stragglers.
    //
    // The number of passes is now set by whichever subject needs the most
    // configurations: the vehicle paint table or the kick leg's opaque/fade
    // pair — each wrapping with its own modulus so none is truncated. The
    // biomes need exactly ONE (see BiomeSystem.warmup).
    //
    // The real frame per config is still mandatory: `compileAsync` walks the
    // COLOUR pass only, and the SHADOW pass is a separate pipeline key, so the
    // shadow variants are compiled by `renderer.render` (synchronously — that
    // is fine, there are only ~70 of them and they are depth-only).
    const vehiclePasses = Math.max(1, vehicleWarmup?.passes ?? 1);
    const legPasses = Math.max(1, legWarmup?.passes ?? 1);
    const crashPasses = Math.max(1, crashWarmup?.passes ?? 1);
    const biomePasses = biomeWarmup ? Math.max(1, biomeWarmup.passes) : 0;
    const passes = Math.max(vehiclePasses, legPasses, crashPasses, biomePasses);
    for (let p = 0; p < passes; p++) {
      // Config 0 carries the biomes as well (they need exactly one config); they
      // are put away as soon as their passes are done, so the later paint/fade
      // configs walk a small scene. The compiled pipelines survive `restore` —
      // only the visibility goes away.
      if (biomeWarmup && p >= biomePasses) {
        biomeWarmup.restore();
        biomeWarmup = null; // already restored; keep `finally` from doing it twice
      }
      biomeWarmup?.beginPass(p);
      vehicleWarmup?.beginPass(p % vehiclePasses);
      legWarmup?.beginPass(p % legPasses);
      crashWarmup?.beginPass(p % crashPasses);
      await phaseAsync(`config${p}:compile`, () => compileParallel(renderer, scene, camera));
      await phaseAsync(`config${p}:render`, async () => {
        renderer.render(scene, camera);
      });
    }
  } catch (err) {
    console.warn("[warmup] pipeline warm-up failed (non-fatal):", err);
  } finally {
    juice.particles.reset();
    vehicleWarmup?.restore();
    coinWarmup?.restore();
    legWarmup?.restore();
    biomeWarmup?.restore();
    crashWarmup?.restore();
    try {
      renderer.setPixelRatio(pixelRatio);
      renderer.setSize(size.x / pixelRatio, size.y / pixelRatio, false);
    } catch {
      /* see above */
    }
    mark("warmup:done");
    (globalThis as { __warmupProfile?: typeof profile }).__warmupProfile = profile;
    // Nothing is disposed: every subject warmed here is a LIVE object (the
    // vehicle pool, the biome props, the kick legs) or shares its geometry and
    // materials with one. That is the whole point — the warmed pipeline and the
    // warmed per-object GPU resources must be the ones gameplay uses.
  }
}
